import axios, {
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';
import { ErrorType } from '../types/errors';
import { ErrorHandler } from '../utils/errorHandler';
import { setCSRFHeader } from '../utils/csrf';

const API_BASE_URL = '/api/v1';

interface RateLimitRetryConfig extends InternalAxiosRequestConfig {
  __rateLimitRetried?: boolean;
}

const SAFE_RATE_LIMIT_RETRY_METHODS = new Set(['get', 'head', 'options']);

export const shouldRetryRateLimitedRequest = (
  config?: AxiosRequestConfig & { __rateLimitRetried?: boolean }
): boolean => {
  const method = String(config?.method || '').toLowerCase();
  return (
    SAFE_RATE_LIMIT_RETRY_METHODS.has(method)
    && config?.__rateLimitRetried !== true
  );
};

export const sessionApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

sessionApi.interceptors.request.use((config) => {
  if (config.method && ['post', 'put', 'patch', 'delete'].includes(config.method.toLowerCase())) {
    setCSRFHeader(config.headers || {});
  }
  return config;
});

sessionApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const apiError = ErrorHandler.classifyError(error);
    const retryConfig = error.config as RateLimitRetryConfig | undefined;

    if (!ErrorHandler.isUnauthorizedError(apiError)) {
      console.error('API Error:', apiError);
    }

    if (
      apiError.type === ErrorType.RATE_LIMIT
      && retryConfig
      && shouldRetryRateLimitedRequest(retryConfig)
    ) {
      retryConfig.__rateLimitRetried = true;
      const delay = ErrorHandler.getRetryDelay(0);
      await new Promise((resolve) => window.setTimeout(resolve, delay));
      return sessionApi.request(retryConfig);
    }

    return Promise.reject(apiError);
  }
);

export const listResults = <T>(payload: unknown): T[] => {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object' && Array.isArray((payload as { results?: unknown }).results)) {
    return (payload as { results: T[] }).results;
  }
  return [];
};

export interface PaginatedResult<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export const paginatedResult = <T>(payload: unknown): PaginatedResult<T> => {
  if (payload && typeof payload === 'object' && Array.isArray((payload as any).results)) {
    return {
      count: Number((payload as any).count || 0),
      next: (payload as any).next || null,
      previous: (payload as any).previous || null,
      results: (payload as any).results,
    };
  }
  const results = Array.isArray(payload) ? payload as T[] : [];
  return { count: results.length, next: null, previous: null, results };
};

export default sessionApi;
